import React, { useState, useEffect, useRef } from "react";
import { GuidedMethodology, GuidedMethodologyStep } from "../../lib/firebase";

interface MethodologyVisualizationProps {
  methodology: GuidedMethodology;
  editable?: boolean;
  onChange?: (steps: GuidedMethodologyStep[]) => void;
}

export const MethodologyVisualization: React.FC<MethodologyVisualizationProps> = ({ methodology, editable = false, onChange }) => {
  const [simulatedInput, setSimulatedInput] = useState<Record<string, string>>({});
  const [highlightedPath, setHighlightedPath] = useState<string[]>([]);
  const [svgDimensions, setSvgDimensions] = useState({ width: 800, height: 600 });
  const [hoveredStep, setHoveredStep] = useState<string | null>(null);
  const [selectedStep, setSelectedStep] = useState<string | null>(null);
  const [draggedStepId, setDraggedStepId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null);
  const [editingStepId, setEditingStepId] = useState<string | null>(null);
  const [newStepTitle, setNewStepTitle] = useState('');
  const [showAddStep, setShowAddStep] = useState(false);
  const [connectFromId, setConnectFromId] = useState<string | null>(null);
  const [positionsOverride, setPositionsOverride] = useState<Record<string, { x: number, y: number }>>({});
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const svgRef = useRef<SVGSVGElement>(null);
  
  // Update dimensions on window resize
  useEffect(() => {
    const updateDimensions = () => {
      const containerWidth = document.querySelector('.methodology-visualization')?.clientWidth || 800;
      setSvgDimensions({
        width: Math.max(containerWidth - 40, 600),
        height: 600
      });
    };
    
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);
  
  if (!methodology || !methodology.steps || methodology.steps.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No steps defined for visualization</p>
      </div>
    );
  }

  // Calculate positions for each step
  const calculateStepPositions = () => {
    const positions: Record<string, { x: number, y: number }> = {};
    const stepWidth = 200;
    const stepHeight = 120; // Increased height for more content
    const horizontalGap = 50;
    const verticalGap = 100; // Increased gap for better visibility
    const maxWidth = svgDimensions.width - stepWidth - 20;

    // First pass - assign positions based on linear flow
    let currentX = 50;
    let currentY = 50;
    let maxY = currentY;

    // Map steps by ID for quick lookup
    const stepsById: Record<string, GuidedMethodologyStep> = {};
    methodology.steps.forEach(step => {
      stepsById[step.id] = step;
    });

    // First, position all steps in a basic grid
    methodology.steps.forEach((step, index) => {
      positions[step.id] = { x: currentX, y: currentY };
      
      // Move to next position
      currentX += stepWidth + horizontalGap;
      
      // Wrap to next row if needed
      if (currentX > maxWidth) {
        currentX = 50;
        currentY += stepHeight + verticalGap;
        maxY = currentY;
      }
    });

    return { positions, stepWidth, stepHeight, maxY: maxY + stepHeight };
  };

  const { positions, stepWidth, stepHeight, maxY } = calculateStepPositions();

  // Function to get the next step IDs for a step
  const getNextStepIds = (step: GuidedMethodologyStep): string[] => {
    if (step.nextStepType === 'Linear' && step.nextStepId) {
      return [step.nextStepId];
    } else if (step.nextStepType === 'Conditional' && step.conditions) {
      return step.conditions
        .filter(condition => condition.nextStepId !== 'end')
        .map(condition => condition.nextStepId);
    }
    return [];
  };

  // Function to get the step number (index + 1)
  const getStepNumber = (stepId: string): number => {
    const index = methodology.steps.findIndex(s => s.id === stepId);
    return index !== -1 ? index + 1 : 0;
  };

  // Function to simulate user flow based on inputs
  const simulateUserFlow = (startStepId: string, userInput: string) => {
    const path: string[] = [startStepId];
    let currentStep = methodology.steps.find(s => s.id === startStepId);
    
    if (!currentStep) return path;
    
    // Simulate up to 10 steps to prevent infinite loops
    for (let i = 0; i < 10; i++) {
      if (currentStep.nextStepType === 'Linear') {
        if (!currentStep.nextStepId) break;
        
        path.push(currentStep.nextStepId);
        currentStep = methodology.steps.find(s => s.id === currentStep!.nextStepId);
        if (!currentStep) break;
      } 
      else if (currentStep.nextStepType === 'Conditional' && currentStep.conditions) {
        // Find matching condition
        const matchingCondition = currentStep.conditions.find(condition => {
          if (condition.condition === 'equals' && userInput === condition.value) return true;
          if (condition.condition === 'contains' && userInput.includes(condition.value)) return true;
          if (condition.condition === 'greater_than' && parseFloat(userInput) > parseFloat(condition.value)) return true;
          if (condition.condition === 'less_than' && parseFloat(userInput) < parseFloat(condition.value)) return true;
          return false;
        });
        
        if (matchingCondition) {
          if (matchingCondition.nextStepId === 'end') break;
          
          path.push(matchingCondition.nextStepId);
          currentStep = methodology.steps.find(s => s.id === matchingCondition.nextStepId);
          if (!currentStep) break;
        } else {
          // No matching condition
          break;
        }
      } else {
        break;
      }
    }
    
    return path;
  };

  // Generate connection paths between steps
  const renderConnections = () => {
    const connections: JSX.Element[] = [];
    
    methodology.steps.forEach(step => {
      if (step.nextStepType === 'Linear' && step.nextStepId) {
        // Linear connection
        if (positions[step.id] && positions[step.nextStepId]) {
          const start = positions[step.id];
          const end = positions[step.nextStepId];
          
          // Calculate path points
          const startX = start.x + stepWidth / 2;
          const startY = start.y + stepHeight;
          const endX = end.x + stepWidth / 2;
          const endY = end.y;
          
          // Create a curved path
          const path = `M ${startX} ${startY} C ${startX} ${startY + 30}, ${endX} ${endY - 30}, ${endX} ${endY}`;
          
          // Check if this connection is part of the highlighted path
          const isHighlighted = highlightedPath.includes(step.id) && 
                               highlightedPath.includes(step.nextStepId) &&
                               highlightedPath.indexOf(step.nextStepId) === highlightedPath.indexOf(step.id) + 1;
          
          connections.push(
            <path 
              key={`${step.id}-${step.nextStepId}`}
              d={path}
              stroke={isHighlighted ? "#ff9800" : "#daed6e"}
              strokeWidth={isHighlighted ? "3" : "2"}
              fill="none"
              markerEnd={isHighlighted ? "url(#arrowhead-highlighted)" : "url(#arrowhead)"}
              className="transition-all duration-300"
            />
          );

          // Add label for linear connection
          const midX = (startX + endX) / 2;
          const midY = (startY + endY) / 2 + 15;

          connections.push(
            <text
              key={`${step.id}-${step.nextStepId}-label`}
              x={midX}
              y={midY}
              textAnchor="middle"
              fontSize="12"
              fill="#666"
              className="connection-label"
            >
              Next
            </text>
          );
        }
      } else if (step.nextStepType === 'Conditional' && step.conditions) {
        // Conditional connections
        step.conditions.forEach((condition, idx) => {
          if (condition.nextStepId === 'end') return;
          
          if (positions[step.id] && positions[condition.nextStepId]) {
            const start = positions[step.id];
            const end = positions[condition.nextStepId];
            
            // Calculate path points with offset for multiple conditions
            const startX = start.x + stepWidth / 2 + (idx - (step.conditions!.length - 1) / 2) * 20;
            const startY = start.y + stepHeight;
            const endX = end.x + stepWidth / 2;
            const endY = end.y;
            
            // Create a curved path
            const path = `M ${startX} ${startY} C ${startX} ${startY + 40}, ${endX} ${endY - 40}, ${endX} ${endY}`;
            
            // Check if this connection is part of the highlighted path
            const isHighlighted = highlightedPath.includes(step.id) && 
                                highlightedPath.includes(condition.nextStepId) &&
                                highlightedPath.indexOf(condition.nextStepId) === highlightedPath.indexOf(step.id) + 1;
            
            connections.push(
              <path 
                key={`${step.id}-${condition.nextStepId}-${idx}`}
                d={path}
                stroke={isHighlighted ? "#ff9800" : "#daed6e"}
                strokeWidth={isHighlighted ? "3" : "2"}
                fill="none"
                markerEnd={isHighlighted ? "url(#arrowhead-highlighted)" : "url(#arrowhead)"}
                className="transition-all duration-300"
              />
            );

            // Add label for conditional connection
            const midX = (startX + endX) / 2;
            const midY = (startY + endY) / 2;
            const conditionText = `${condition.condition} "${condition.value}"`;

            connections.push(
              <g key={`${step.id}-${condition.nextStepId}-${idx}-label`}>
                <rect
                  x={midX - 60}
                  y={midY - 10}
                  width={120}
                  height={20}
                  rx={10}
                  ry={10}
                  fill="white"
                  stroke={isHighlighted ? "#ff9800" : "#daed6e"}
                  strokeWidth="1"
                />
                <text
                  x={midX}
                  y={midY + 5}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#666"
                  className="connection-label"
                >
                  {conditionText.length > 20 ? conditionText.substring(0, 18) + "..." : conditionText}
                </text>
              </g>
            );
          }
        });
      }
    });
    
    return connections;
  };

  // Handle simulated input for a step
  const handleSimulatedInput = (stepId: string, value: string) => {
    const newInputs = { ...simulatedInput, [stepId]: value };
    setSimulatedInput(newInputs);
    
    // Simulate the flow based on this input
    const path = simulateUserFlow(stepId, value);
    setHighlightedPath(path);
  };

  // Clear all simulations
  const clearSimulations = () => {
    setSimulatedInput({});
    setHighlightedPath([]);
  };

  // Handle step click to show details
  const handleStepClick = (stepId: string) => {
    setSelectedStep(selectedStep === stepId ? null : stepId);
  };

  // Render step details panel
  const renderStepDetails = () => {
    if (!selectedStep) return null;
    
    const step = methodology.steps.find(s => s.id === selectedStep);
    if (!step) return null;
    
    return (
      <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
        <h4 className="font-medium text-md mb-2">Step {getStepNumber(step.id)} Details</h4>
        
        <div className="space-y-3">
          <div>
            <span className="text-sm font-medium text-gray-700 block">Title:</span>
            <span className="text-sm">{step.title}</span>
          </div>
          
          <div>
            <span className="text-sm font-medium text-gray-700 block">Description:</span>
            <p className="text-sm">{step.description || "No description provided"}</p>
          </div>
          
          {step.toolSuggestions && step.toolSuggestions.length > 0 && (
            <div>
              <span className="text-sm font-medium text-gray-700 block">Tool Suggestions:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {step.toolSuggestions.map((tool, i) => {
                  const toolUrl = step.toolSuggestionUrls?.[tool];
                  
                  if (toolUrl) {
                    return (
                      <a 
                        key={i}
                        href={toolUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs bg-gray-100 px-2 py-1 rounded hover:bg-gray-200 transition-colors flex items-center gap-1"
                      >
                        {tool}
                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                          <polyline points="15 3 21 3 21 9"></polyline>
                          <line x1="10" y1="14" x2="21" y2="3"></line>
                        </svg>
                      </a>
                    );
                  }
                  
                  return (
                    <span key={i} className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {tool}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          
          {step.commandExamples && step.commandExamples.length > 0 && (
            <div>
              <span className="text-sm font-medium text-gray-700 block">Command Examples:</span>
              <div className="mt-1 space-y-1">
                {step.commandExamples.map((cmd, i) => (
                  <div key={i} className="text-xs font-mono bg-gray-800 text-white p-2 rounded overflow-x-auto">
                    {cmd}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {step.requiresUserInput && (
            <div>
              <span className="text-sm font-medium text-gray-700 block">User Input:</span>
              <span className="text-sm">{step.inputFieldLabel || "Required"} ({step.inputDataType || "Text"})</span>
            </div>
          )}
          
          {step.nextStepType === 'Conditional' && step.conditions && step.conditions.length > 0 && (
            <div>
              <span className="text-sm font-medium text-gray-700 block">Conditions:</span>
              <ul className="mt-1 list-disc pl-5 space-y-1">
                {step.conditions.map((condition, i) => (
                  <li key={i} className="text-sm">
                    If {condition.condition} "{condition.value}" → 
                    {condition.nextStepId === 'end' 
                      ? ' End' 
                      : ` Step ${getStepNumber(condition.nextStepId)}`}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {step.guidanceNotes && (
            <div>
              <span className="text-sm font-medium text-gray-700 block">Guidance Notes:</span>
              <p className="text-sm">{step.guidanceNotes}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Add utility for grid snapping
  const GRID_SIZE = 32;
  function snapToGrid(x: number, y: number) {
    return {
      x: Math.round(x / GRID_SIZE) * GRID_SIZE,
      y: Math.round(y / GRID_SIZE) * GRID_SIZE,
    };
  }

  // Auto-layout algorithm (top-to-bottom, spaced, no overlap)
  function autoLayoutSteps(steps: GuidedMethodologyStep[], svgWidth: number): Record<string, { x: number; y: number }> {
    const stepWidth = 200;
    const stepHeight = 120;
    const horizontalGap = 50;
    const verticalGap = 100;
    const maxPerRow = Math.max(1, Math.floor((svgWidth - 50) / (stepWidth + horizontalGap)));
    const positions: Record<string, { x: number; y: number }> = {};
    let x = 50, y = 50, col = 0;
    steps.forEach((step, idx) => {
      positions[step.id] = { x, y };
      col++;
      if (col >= maxPerRow) {
        col = 0;
        x = 50;
        y += stepHeight + verticalGap;
      } else {
        x += stepWidth + horizontalGap;
      }
    });
    return positions;
  }

  // On mount, auto-layout steps if no positions exist
  useEffect(() => {
    if (!editable) return;
    if (!methodology.steps.some(s => s.position)) {
      const autoPositions = autoLayoutSteps(methodology.steps, svgDimensions.width);
      const updatedSteps = methodology.steps.map(s => ({ ...s, position: autoPositions[s.id] }));
      onChange?.(updatedSteps);
    }
    // eslint-disable-next-line
  }, [editable, methodology.steps.length, svgDimensions.width]);

  // Drag logic: update step.position in steps array
  const handleStepMouseDown = (e: React.MouseEvent, stepId: string) => {
    if (!editable) return;
    setDraggedStepId(stepId);
    const step = methodology.steps.find(s => s.id === stepId);
    const pos = step?.position || positions[stepId];
    setDragOffset({ x: e.clientX - pos.x, y: e.clientY - pos.y });
  };

  useEffect(() => {
    if (!editable || !draggedStepId) return;
    const handleMouseMove = (e: MouseEvent) => {
      const { x, y } = snapToGrid(e.clientX - (dragOffset?.x || 0), e.clientY - (dragOffset?.y || 0));
      const updatedSteps = methodology.steps.map(s =>
        s.id === draggedStepId ? { ...s, position: { x, y } } : s
      );
      onChange?.(updatedSteps);
    };
    const handleMouseUp = () => {
      setDraggedStepId(null);
      setDragOffset(null);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggedStepId, dragOffset, editable, methodology.steps, onChange]);

  // Get step position (from step.position or fallback to auto layout)
  const getStepPosition = (stepId: string) => {
    const step = methodology.steps.find(s => s.id === stepId);
    return step?.position || positions[stepId];
  };

  // Re-layout button handler
  const handleRelayout = () => {
    const autoPositions = autoLayoutSteps(methodology.steps, svgDimensions.width);
    const updatedSteps = methodology.steps.map(s => ({ ...s, position: autoPositions[s.id] }));
    onChange?.(updatedSteps);
  };

  // Zoom/pan handlers
  const handleWheel = (e: React.WheelEvent) => {
    if (!editable) return;
    e.preventDefault();
    setZoom(z => Math.max(0.5, Math.min(2, z - e.deltaY * 0.001)));
  };
  let lastPan = { x: 0, y: 0 };
  const handleSvgMouseDown = (e: React.MouseEvent) => {
    if (!editable || e.button !== 1) return; // middle mouse
    lastPan = { x: e.clientX, y: e.clientY };
    const handleMove = (ev: MouseEvent) => {
      setPan(p => ({ x: p.x + (ev.clientX - lastPan.x), y: p.y + (ev.clientY - lastPan.y) }));
      lastPan = { x: ev.clientX, y: ev.clientY };
    };
    const handleUp = () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };

  // Add new step
  const handleAddStep = () => {
    if (!newStepTitle.trim()) return;
    const newStep: GuidedMethodologyStep = {
      id: `step-${Date.now()}`,
      title: newStepTitle,
      description: '',
      toolSuggestions: [],
      commandExamples: [],
      guidanceNotes: '',
      requiresUserInput: false,
      nextStepType: 'Linear',
      nextStepId: undefined,
      conditions: []
    };
    const updatedSteps = [...methodology.steps, newStep];
    onChange?.(updatedSteps);
    setNewStepTitle('');
    setShowAddStep(false);
  };

  // Remove step
  const handleRemoveStep = (stepId: string) => {
    const updatedSteps = methodology.steps.filter(s => s.id !== stepId);
    // Remove connections to this step
    updatedSteps.forEach(s => {
      if (s.nextStepId === stepId) s.nextStepId = undefined;
      if (s.conditions) s.conditions = s.conditions.filter(c => c.nextStepId !== stepId);
    });
    onChange?.(updatedSteps);
  };

  // Edit step title inline
  const handleEditStepTitle = (stepId: string, title: string) => {
    const updatedSteps = methodology.steps.map(s =>
      s.id === stepId ? { ...s, title } : s
    );
    onChange?.(updatedSteps);
  };

  // Start connecting steps
  const handleStartConnect = (fromId: string) => {
    setConnectFromId(fromId);
  };
  // Finish connecting steps
  const handleFinishConnect = (toId: string) => {
    if (!connectFromId || connectFromId === toId) return setConnectFromId(null);
    const updatedSteps = methodology.steps.map(s =>
      s.id === connectFromId ? { ...s, nextStepId: toId, nextStepType: 'Linear' } : s
    );
    onChange?.(updatedSteps);
    setConnectFromId(null);
  };

  // Render steps with edit controls
  const renderSteps = () => (
    methodology.steps.map((step, idx) => {
      const pos = getStepPosition(step.id);
      return (
        <g key={step.id} transform={`translate(${pos.x},${pos.y})`} style={{ cursor: editable ? 'move' : 'default' }}>
          <rect
            width={stepWidth}
            height={stepHeight}
            rx={16}
            fill={editingStepId === step.id ? '#fffbe6' : '#fff'}
            stroke={connectFromId === step.id ? '#ff9800' : '#daed6e'}
            strokeWidth={3}
            onMouseDown={e => handleStepMouseDown(e, step.id)}
            onClick={() => editable && setEditingStepId(step.id)}
            style={{ filter: editable ? 'drop-shadow(0 2px 8px #daed6e33)' : undefined }}
          />
          {editingStepId === step.id && editable ? (
            <foreignObject x={10} y={10} width={stepWidth - 20} height={stepHeight - 20}>
              <div className="flex flex-col gap-2 p-1">
                <input
                  className="border rounded px-2 py-1 text-sm font-bold"
                  value={step.title}
                  onChange={e => handleEditStepTitle(step.id, e.target.value)}
                  autoFocus
                />
                <button className="text-xs text-blue-600 underline" onClick={() => setEditingStepId(null)}>Done</button>
                <button className="text-xs text-red-500 underline mt-2" onClick={() => handleRemoveStep(step.id)}>Delete Step</button>
              </div>
            </foreignObject>
          ) : (
            <text x={stepWidth / 2} y={40} textAnchor="middle" fontWeight="bold" fontSize="16" fill="#222">
              {step.title}
            </text>
          )}
          {editable && (
            <g>
              <circle
                cx={stepWidth - 18}
                cy={stepHeight / 2}
                r={12}
                fill={connectFromId === step.id ? '#ff9800' : '#daed6e'}
                stroke="#fff"
                strokeWidth={2}
                style={{ cursor: 'pointer' }}
                onClick={() => connectFromId ? handleFinishConnect(step.id) : handleStartConnect(step.id)}
              />
              <text
                x={stepWidth - 18}
                y={stepHeight / 2 + 5}
                textAnchor="middle"
                fontSize="16"
                fill="#222"
                style={{ pointerEvents: 'none' }}
              >
                →
              </text>
            </g>
          )}
        </g>
      );
    })
  );

  return (
    <div className="methodology-visualization relative">
      {editable && (
        <div className="absolute left-4 top-4 z-20 flex gap-2">
          <button
            className="px-3 py-1 rounded bg-[#daed6e] text-black font-semibold shadow hover:bg-[#c5d85c]"
            onClick={() => setShowAddStep(v => !v)}
          >
            {showAddStep ? 'Cancel' : 'Add Step'}
          </button>
          <button
            className="px-3 py-1 rounded bg-blue-500 text-white font-semibold shadow hover:bg-blue-600"
            onClick={handleRelayout}
          >
            Re-layout
          </button>
          {showAddStep && (
            <div className="flex gap-2 items-center">
              <input
                className="border rounded px-2 py-1 text-sm"
                placeholder="Step title"
                value={newStepTitle}
                onChange={e => setNewStepTitle(e.target.value)}
                autoFocus
              />
              <button
                className="px-3 py-1 rounded bg-green-500 text-white font-semibold shadow hover:bg-green-600"
                onClick={handleAddStep}
              >
                Add
              </button>
            </div>
          )}
        </div>
      )}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <h3 className="font-['Space_Grotesk'] font-medium text-lg text-black mb-2">Methodology Flow Diagram</h3>
          <div className="flex items-center gap-2">
            {highlightedPath.length > 0 && (
              <button 
                onClick={clearSimulations}
                className="text-sm text-gray-600 hover:text-gray-900 underline"
              >
                Clear Simulations
              </button>
            )}
            <div className="text-xs bg-gray-100 px-2 py-1 rounded">
              {methodology.steps.length} steps
            </div>
          </div>
        </div>
        <p className="text-sm text-gray-600">
          This diagram shows the flow between steps in the methodology. Click on any step to view details. Use the input fields below to simulate user responses and see the resulting path.
        </p>
      </div>
      
      <div className="bg-white rounded-lg p-4 overflow-auto border border-gray-200 shadow-inner">
        <svg 
          ref={svgRef}
          width={svgDimensions.width}
          height={Math.max(svgDimensions.height, maxY + 100)}
          style={{ width: '100%', height: 'auto', minHeight: 600, cursor: editable ? 'grab' : 'default' }}
          onWheel={handleWheel}
          onMouseDown={handleSvgMouseDown}
        >
          <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
            <defs>
              <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="7"
                refX="9"
                refY="3.5"
                orient="auto"
              >
                <polygon points="0 0, 10 3.5, 0 7" fill="#daed6e" />
              </marker>
              <marker
                id="arrowhead-highlighted"
                markerWidth="10"
                markerHeight="7"
                refX="9"
                refY="3.5"
                orient="auto"
              >
                <polygon points="0 0, 10 3.5, 0 7" fill="#ff9800" />
              </marker>
            </defs>
            
            {renderConnections()}
            {renderSteps()}
          </g>
        </svg>
        
        {/* Step details panel */}
        {renderStepDetails()}
        
        {/* Simulation controls */}
        <div className="mt-8 border-t border-gray-200 pt-4">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-medium text-md">Simulate User Inputs</h4>
            {highlightedPath.length > 0 && (
              <div className="text-sm bg-[#fff8e1] border border-[#ff9800] px-3 py-1 rounded-md">
                Simulating path: {highlightedPath.map(id => getStepNumber(id)).join(' → ')}
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {methodology.steps
              .filter(step => step.requiresUserInput)
              .map(step => {
                const isInPath = highlightedPath.includes(step.id);
                const stepNumber = getStepNumber(step.id);
                
                return (
                  <div 
                    key={step.id} 
                    className={`p-3 border rounded-md transition-all duration-300 ${
                      isInPath ? 'bg-[#fff8e1] border-[#ff9800]' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <label className="block mb-2 text-sm font-medium">
                      Step {stepNumber}: {step.title} {step.inputFieldLabel ? `(${step.inputFieldLabel})` : ''}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={simulatedInput[step.id] || ''}
                        onChange={(e) => handleSimulatedInput(step.id, e.target.value)}
                        placeholder={step.inputDataType === 'Number' ? "Enter a number..." : "Enter a value..."}
                        className="flex h-9 rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring flex-1"
                      />
                      <button
                        onClick={() => handleSimulatedInput(step.id, simulatedInput[step.id] || '')}
                        className="px-3 py-1 bg-[#daed6e] rounded-md hover:bg-[#c5d85c] text-sm"
                      >
                        Simulate
                      </button>
                    </div>
                    
                    {step.nextStepType === 'Conditional' && step.conditions && step.conditions.length > 0 && (
                      <div className="mt-2 text-xs text-gray-600">
                        <p className="font-medium mb-1">Possible conditions:</p>
                        <ul className="list-disc pl-4 space-y-1">
                          {step.conditions.map((condition, idx) => (
                            <li key={idx}>
                              If {condition.condition} "{condition.value}" → 
                              {condition.nextStepId === 'end' 
                                ? ' End' 
                                : ` Step ${getStepNumber(condition.nextStepId)}`}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
          
          {methodology.steps.filter(step => step.requiresUserInput).length === 0 && (
            <div className="text-center py-4 text-gray-500">
              <p>This methodology doesn't have any steps that require user input.</p>
            </div>
          )}
        </div>
      </div>
      {editable && (
        <div className="absolute right-4 bottom-4 bg-[#fffbe6] border border-[#daed6e] rounded-lg px-4 py-2 shadow text-sm text-gray-700">
          <b>Edit Mode:</b> Drag steps, click to edit, connect with arrows, add/remove steps. <br />
          <span className="text-xs">(More advanced editing coming soon!)</span>
        </div>
      )}
    </div>
  );
}; 